import React, { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import Button from "../../components/ui/Button";

const HeroSection = () => {
  const containerRef = useRef(null);

  // Directly track the window's scrollY so animation starts immediately without any offset delay
  const { scrollY } = useScroll();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  // Snappy spring configuration
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 300,
    damping: 25,
  });

  const leftImageY = useTransform(
    smoothProgress,
    [0, 0.3, 1],
    [0, -1000, -1000],
  );
  const rightImageY = useTransform(
    smoothProgress,
    [0, 0.3, 1],
    [0, 1000, 1000],
  );

  return (
    <section
      ref={containerRef}
      className="relative w-full overflow-hidden bg-white pb-10 lg:pb-20 font-bricolage"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
        <img
          src="/images/img_group_163.png"
          alt="Background Pattern"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative">
        {/* Left Side Image - Scroll based movement */}
        <motion.div
          style={{ y: leftImageY }} // animate ki jagah style use hoga
          className="absolute left-[4%] 2xl:left-[10%] top-[50%] w-[120px] h-[120px] 2xl:w-[184px] 2xl:h-[184px] z-10"
        >
          <img
            src="/images/img_rectangle_53_242x242.png"
            alt="Decorative Cell"
            className="w-full h-full object-cover rounded-[20px]"
          />
        </motion.div>

        {/* Right Side Image - Scroll based movement */}
        <motion.div
          style={{ y: rightImageY }} // animate ki jagah style use hoga
          className="absolute right-[4%] 2xl:right-[10%] top-[-50%] w-[120px] h-[120px] 2xl:w-[184px] 2xl:h-[184px] z-10"
        >
          <img
            src="/images/img_rectangle_53.png"
            alt="Decorative Cell"
            className="w-full h-full object-cover rounded-[20px]"
          />
        </motion.div>

        {/* --- Main Content Section --- */}
        <div className="relative z-20 flex flex-col items-center justify-center px-4 text-center mt-[100px] xl:mt-[214px] mb-[100px] xl:mb-[144px]">
          <h1 className="text-[24px] md:text-[30px] lg:text-[40px] 2xl:text-[60px] font-extrabold leading-[1.1] text-[#183076] max-w-7xl">
            Advancing Diagnostics. <br className="hidden md:block" />
            Enabling Global Research.
          </h1>

          <p className="mt-6 text-[14px] md:text-[18px] xl:text-[24px] max-w-[1099px] leading-relaxed">
            A diagnostic laboratory delivering ethically sourced human
            biospecimens with comprehensive clinical annotation, global-ready
            procurement, validated SOP-driven workflows, and uncompromised cold
            chain integrity.
          </p>

          {/* <Button
            text="Explore More"
            variant="outlined"
            to="/biospecimens-services"
            className="mt-[20px] lg:px-[40px] lg:py-[18px] text-[16px] md:text-[20px] xl:text-[22px] border border-[#183076] text-[#183076] hover:bg-[#183076] hover:text-white transition-all rounded-full"
          /> */}
          <Button
            text="Explore More"
            variant="outlined"
            to="/biospecimens-services"
            className="mt-[20px] 2xl:px-[40px] 2xl:py-[18px] text-[16px] md:text-[20px] 2xl:text-[22px] border border-[#183076] text-[#183076] hover:bg-[#183076] hover:text-white transition-all rounded-full"
          />
        </div>
      </div>
      {/* --- Service Highlight Cards --- */}
      <div className="relative z-20 mx-auto mt-20 px-[20px] 2xl:px-[60px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              title: "Advance Research with Diagnostic-Grade Biospecimens",
              img: "/images/img_rectangle_55.png",
              desc: "Access ethically sourced human biospecimens with comprehensive clinical annotation—supported by global procurement, validated SOPs, and stringent cold chain management.",
            },
            {
              title: "Precision Diagnostics. Reliable Results.",
              img: "/images/img_rectangle_56.png",
              desc: "Comprehensive testing and molecular diagnostics supported by certified labs and an experienced medical team.",
            },
          ].map((card, i) => (
            <div
              key={i}
              className="relative group overflow-hidden rounded-[20px] md:rounded-[40px] shadow-2xl aspect-[4/5] md:aspect-square"
            >
              <img
                src={card.img}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                alt={card.title}
              />

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-[15px] md:rounded-[30px] p-4 xl:p-8 shadow-lg  md:min-h-[45%] flex flex-col justify-between">
                <div className="flex-1">
                  {" "}
                  {/* Ye div extra space consume karega taaki niche ka button align rahe */}
                  <h3 className="text-[20px] md:text-[22px] lg:text-[30px] 2xl:text-[40px] font-extrabold text-[#183076] leading-tight">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-[12px] sm:text-[14px] lg:text-[18px] xl:text-[24px] text-gray-500">
                    {card.desc}
                  </p>
                </div>
                <div>
                  <Button
                    text="Book Now"
                    variant="outlined"
                    to="/rfq"
                    className="mt-[20px] 2xl:px-[40px] 2xl:py-[18px] text-[16px] md:text-[20px] 2xl:text-[22px] border border-[#183076] text-[#183076] hover:bg-[#183076] hover:text-white transition-all rounded-full"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
